import { Button, CardContainer } from 'finara-components';

import { PaymentWizardState } from '@/types/payment';

interface ReviewPaymentProps {
  wizardState: PaymentWizardState;
  onConfirm: () => void;
}

export const ReviewPayment: React.FC<ReviewPaymentProps> = ({ wizardState, onConfirm }) => {
  const { source, data, summary } = wizardState;

  if (!source || !data || !summary) {
    return <div>Error: Missing payment data</div>;
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  const formatDateTime = () => {
    if (data.executionType === 'immediate') {
      return 'Inmediato';
    }

    if (data.scheduledDate && data.scheduledTime) {
      const date = new Date(`${data.scheduledDate}T${data.scheduledTime}`);
      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }

    return 'No especificado';
  };

  const getAccountTypeName = (type: string) => {
    const typeMap: Record<string, string> = {
      organization: 'Cuenta Principal',
      savings_organization: 'Cuenta de Ahorros',
      payroll: 'Nómina',
      savings: 'Ahorros',
    };
    return typeMap[type] ?? type;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-brand-white mb-2">Revisión del Pago</h2>
        <p className="text-muted">
          Verifica que todos los datos sean correctos antes de proceder
        </p>
      </div>

      <div className="grid grid-cols-1 lg:!grid-cols-2 auto-rows-[minmax(1,1fr)] gap-8">
        {/* Origin Column */}
        <div className="h-full">
          <h3 className="text-lg font-semibold text-brand-white mb-4">Origen</h3>

          <CardContainer className="p-4 mb-4">
            <div className="space-y-3">
              <div>
                <div className="text-sm text-muted">Cliente</div>
                <div className="font-medium text-brand-white">{source.clientName}</div>
              </div>

              <div>
                <div className="text-sm text-muted">Cuenta de Origen</div>
                <div className="font-medium text-brand-white">
                  {getAccountTypeName(source.selectedAccount.type)}
                </div>
                <div className="text-sm text-muted mb-8">
                  {source.selectedAccount.clabe} • {source.selectedAccount.currency}
                </div>
              </div>
            </div>
          </CardContainer>

          <h3 className="text-lg font-semibold text-brand-white mb-4">Detalles del Pago</h3>

          <CardContainer className="p-4">
            <div className="space-y-3">
              <div>
                <div className="text-sm text-muted">Monto</div>
                <div className="text-xl font-bold text-brand-white">{formatCurrency(data.amount)}</div>
              </div>

              <div>
                <div className="text-sm text-muted">Concepto</div>
                <div className="font-medium text-brand-white">{data.concept}</div>
              </div>
            </div>
          </CardContainer>
        </div>

        {/* Destination Column */}
        <div>
          <h3 className="text-lg font-semibold text-brand-white mb-4">Destino</h3>

          <CardContainer className="p-4 mb-4">
            <div className="space-y-3">
              <div>
                <div className="text-sm text-muted">Beneficiario</div>
                <div className="font-medium text-brand-white">{data.beneficiary.fullName}</div>
              </div>

              <div>
                <div className="text-sm text-muted">CLABE</div>
                <div className="font-medium text-brand-white">{data.beneficiary.clabe}</div>
              </div>

              <div>
                <div className="text-sm text-muted">RFC</div>
                <div className="font-medium text-brand-white">{data.beneficiary.rfc}</div>
              </div>
            </div>
          </CardContainer>

          <h3 className="text-lg font-semibold text-brand-white mb-4">Ejecución</h3>

          <CardContainer className="p-4">
            <div className="space-y-3">
              <div>
                <div className="text-sm text-muted">Tipo</div>
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-white/10 text-brand-white">
                  {data.executionType === 'immediate' ? 'Inmediato' : 'Programado'}
                </div>
              </div>

              <div>
                <div className="text-sm text-muted">Fecha y Hora</div>
                <div className="font-medium text-brand-white">{formatDateTime()}</div>
              </div>
            </div>
          </CardContainer>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-brand-white mb-4">Resumen Financiero</h3>

        <CardContainer className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted">Monto del Pago</span>
              <span className="font-medium text-brand-white">
                {formatCurrency(summary.paymentAmount)}
              </span>
            </div>

            <div className="border-t border-border pt-4">
              <div className="flex justify-between">
                <span className="text-lg font-semibold text-brand-white">Total</span>
                <span className="text-xl font-bold text-brand-white">
                  {formatCurrency(summary.total)}
                </span>
              </div>
            </div>
          </div>
        </CardContainer>
      </div>

      {/* Navigation */}
      <div className="flex justify-end mt-8">
        <Button variant="black" size="large" onClick={onConfirm} className="min-w-32">
          Siguiente
        </Button>
      </div>
    </div>
  );
};
